import React, { useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import withAuth from '../../../utils/hocs/withAuth';
import withSidebar from '../../../utils/hocs/withSidebar';
import SCOPE from '../../../utils/gate/scope';
import withPermission from '../../../utils/hocs/withPermission';
import useNotification from '../../../utils/hooks/useNotification';
import campCreationSchema from '../../../validations/products/camp/creationSchema';
import buildTabs from '../../../utils/tools/buildTabs';
import useForm from '../../../utils/hooks/useForm';

// Services
import useCampService from '../../../services/campService';

// Icons
import { ReactComponent as FileIcon } from '../../../assets/icons/file.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import { ReactComponent as PictureIcon } from '../../../assets/icons/picture.svg';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info.svg';

// Components
import PageWrapper from '../../../components/layout/PageWrapper/PageWrapper';
import General from '../../../components/views/establishment/Products/Camps/Sections/General/General';
import Format from '../../../components/views/establishment/Products/Camps/Sections/Format/Format';
import LayoutWithTabs from '../../../components/layout/LayoutWithTabs/LayoutWithTabs';
import Medias from '../../../components/views/establishment/Products/Camps/Sections/Medias/Medias';
import Features from '../../../components/views/establishment/Products/Camps/Sections/Features/Features';
import SkeletonLoader from '../../../components/molecules/SkeletonLoader/SkeletonLoader';
import Button from '../../../components/atoms/Button/Button';
import Translations from '../../../components/views/establishment/Products/Common/Translations/Translations';

function CampCreation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  const [initialValues] = useState({
    isNovelty: false,
    name: '',
    sport: null,
    type: null,
    duration: 1,
    startDay: null,
    format: null,
    features: [],
    tags: [],
    mainImageMedia: null,
    sliderImageMedias: [],
    videoUrl: '',

    // english content
    title: '',
    description: '',
  });

  const { postCamp } = useCampService();

  const { isLoading: isPostCampLoading, mutate: postCampMutate } = useMutation(
    (values) => postCamp(values),
    {
      onSuccess: () => {
        navigate('/products/camps', {
          state: 'camps.creation.form.success',
        });
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'camps.creation.form.error'),
          color: 'error',
        });
      },
    },
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        postCampMutate({
          isNovelty: values.isNovelty,
          name: values.name,
          sport: values.sport?.value,
          type: values.type?.value,
          duration: values.duration,
          startDay: values.startDay?.value,
          format: values.format?.value,
          features: values.features?.map((feature) => feature.value) || [],
          tags: values.tags?.map((tag) => tag.value) || [],
          mainImageMediaId: values.mainImageMedia?.id,
          sliderImageMediaIds: values.sliderImageMedias?.map((media) => media.id),
          videoUrl: values.videoUrl || null,

          // english content
          englishTitle: values.title,
          englishDescription: values.description,
        });
      }
    },
    validationSchema: campCreationSchema,
    validateOnBlur: true,
  });

  const getTabComponent = (TabComponent, props) => (
    <TabComponent
      formik={{
        values: formik.values,
        handleChange: formik.handleChange,
        handleBlur: formik.handleBlur,
        setFieldValue: formik.setFieldValue,
        setFieldTouched: formik.setFieldTouched,
        touched: formik.touched,
        errors: formik.errors,
      }}
      {...props}
    />
  );

  const tabs = buildTabs([
    {
      label: 'camps.marketing.sections.general',
      component: getTabComponent(General, {}),
      icon: <FileIcon />,
    },
    {
      label: 'camps.marketing.sections.format',
      component: getTabComponent(Format, { isCreation: true }),
      icon: <CalendarIcon />,
    },
    {
      label: 'camps.creation.sections.translations',
      component: getTabComponent(
        Translations,
        { warningMessage: t('camps.creation.englishRequired'), productType: 'camps' },
      ),
      icon: <FileIcon />,
    },
    {
      label: 'camps.marketing.sections.medias',
      component: getTabComponent(Medias, {}),
      icon: <PictureIcon />,
    },
    {
      label: 'camps.marketing.sections.features',
      component: getTabComponent(Features, {}),
      icon: <InfoIcon />,
    },
  ]);

  return (
    <PageWrapper>
      <LayoutWithTabs
        tabs={tabs}
        breadcrumb={[
          { label: t('camps.title'), href: '/products/camps' },
        ]}
        title={t('camps.add')}
        skeletonLoader={(
          <>
            <SkeletonLoader className="m-b-30" height={30} />

            {tabs?.map((_, index) => (
              <React.Fragment key={index}>
                <SkeletonLoader className="m-b-20" height={50} />
                <SkeletonLoader className="m-b-50" height={200} />
              </React.Fragment>
            ))}
          </>
        )}
        moreContent={(
          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isPostCampLoading}
              disabled={!formik.dirty}
            >
              {t('camps.creation.form.submit')}
            </Button>
          </div>
        )}
      />
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(CampCreation, { withTabsBar: true }),
    {
      scopes: [SCOPE.create_ownEst_camp],
    },
  ),
);
